import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import RFQFilters from './components/RFQFilters';
import RFQSummaryCards from './components/RFQSummaryCards';
import RFQTable from './components/RFQTable';
import RFQDetailsModal from './components/RFQDetailsModal';

const RFQManagement = () => {
  const [rfqs, setRfqs] = useState([]);
  const [filteredRfqs, setFilteredRfqs] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    dateRange: ''
  });
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [summaryData, setSummaryData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Mock RFQ data
  const mockRfqs = [
    {
      id: 1,
      rfqNumber: "RFQ-2024-001",
      serviceCategory: "Infrastructure Assessment",
      submissionDate: "2024-09-15T10:30:00Z",
      status: "quoted",
      salesRep: "Sarah Nakamya",
      estimatedResponse: "2024-09-22T17:00:00Z",
      quotedAmount: 15000000,
      priority: "high",
      projectTimeline: "3 months",
      requirements: `We require a comprehensive ICT infrastructure assessment and upgrade plan for our organization. The scope includes:\n\n• Network infrastructure evaluation\n• Server and storage assessment\n• Security audit and recommendations\n• Cloud migration strategy\n• Staff training requirements\n\nThe project should be completed within 3 months with detailed documentation and implementation roadmap.`,
      attachments: [
        { name: "Current_Network_Diagram.pdf", size: "2.4 MB" },
        { name: "IT_Requirements_Document.docx", size: "1.8 MB" }
      ],
      messages: [
        {
          sender: "Sarah Nakamya",
          content: "Thank you for your RFQ submission. We\'re reviewing your requirements and will have a detailed quote ready soon.",
          timestamp: "2024-09-16T09:15:00Z"
        },
        {
          sender: "You",
          content: "Could you please clarify the timeline for the security audit component?",
          timestamp: "2024-09-17T14:30:00Z"
        },
        {
          sender: "Sarah Nakamya",
          content: "The security audit will be completed within the first 4 weeks of the project. I\'ve included detailed timeline in the quote.",
          timestamp: "2024-09-18T11:45:00Z"
        }
      ],
      lastUpdated: "2024-09-18T11:45:00Z"
    },
    {
      id: 2,
      rfqNumber: "RFQ-2024-002",
      serviceCategory: "Software Development",
      submissionDate: "2024-09-12T14:20:00Z",
      status: "under_review",
      salesRep: "James Okello",
      estimatedResponse: "2024-09-25T17:00:00Z",
      priority: "medium",
      projectTimeline: "6 months",
      requirements: "Custom web application development for inventory management system with mobile app integration.",
      messages: [
        {
          sender: "James Okello",
          content: "We\'ve received your RFQ and our development team is analyzing the technical requirements.",
          timestamp: "2024-09-13T10:00:00Z"
        }
      ],
      lastUpdated: "2024-09-13T10:00:00Z"
    },
    {
      id: 3,
      rfqNumber: "RFQ-2024-003",
      serviceCategory: "Networking Solutions",
      submissionDate: "2024-09-10T09:45:00Z",
      status: "accepted",
      salesRep: "Grace Namuli",
      estimatedResponse: "2024-09-20T17:00:00Z",
      quotedAmount: 8500000,
      priority: "normal",
      projectTimeline: "2 months",
      requirements: "Office network setup for 50 employees including WiFi, switches, and security configuration.",
      messages: [
        {
          sender: "Grace Namuli",
          content: "Congratulations! Your quote has been accepted. Our team will contact you to schedule the implementation.",
          timestamp: "2024-09-19T16:30:00Z"
        }
      ],
      lastUpdated: "2024-09-19T16:30:00Z"
    },
    {
      id: 4,
      rfqNumber: "RFQ-2024-004",
      serviceCategory: "Consultancy Services",
      submissionDate: "2024-09-08T11:15:00Z",
      status: "submitted",
      salesRep: "Michael Ssebunya",
      estimatedResponse: "2024-09-28T17:00:00Z",
      priority: "low",
      projectTimeline: "1 month",
      requirements: "Digital transformation strategy consultation for small manufacturing company.",
      messages: [],
      lastUpdated: "2024-09-08T11:15:00Z"
    },
    {
      id: 5,
      rfqNumber: "RFQ-2024-005",
      serviceCategory: "System Integration",
      submissionDate: "2024-09-05T16:30:00Z",
      status: "declined",
      salesRep: "Patricia Achieng",
      estimatedResponse: "2024-09-15T17:00:00Z",
      priority: "medium",
      projectTimeline: "4 months",
      requirements: "ERP system integration with existing accounting software and inventory management.",
      messages: [
        {
          sender: "Patricia Achieng",
          content: "After careful review, we're unable to proceed with this project due to technical constraints. We'd be happy to discuss alternative solutions.",
          timestamp: "2024-09-14T13:20:00Z"
        }
      ],
      lastUpdated: "2024-09-14T13:20:00Z"
    }
  ];

  // Initialize data
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setRfqs(mockRfqs);
      setFilteredRfqs(mockRfqs);
      
      // Calculate summary data
      const totalRFQs = mockRfqs?.length;
      const pendingReview = mockRfqs?.filter(rfq => rfq?.status === 'under_review' || rfq?.status === 'submitted')?.length;
      const quoted = mockRfqs?.filter(rfq => rfq?.status === 'quoted')?.length;
      const accepted = mockRfqs?.filter(rfq => rfq?.status === 'accepted')?.length;
      const conversionRate = totalRFQs > 0 ? Math.round((accepted / totalRFQs) * 100) : 0;
      
      setSummaryData({
        totalRFQs,
        pendingReview,
        quoted,
        conversionRate
      });
      
      setIsLoading(false);
    }, 1000);
  }, []);

  // Filter RFQs based on current filters
  useEffect(() => {
    let filtered = [...rfqs];

    if (filters?.search) {
      filtered = filtered?.filter(rfq => 
        rfq?.rfqNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        rfq?.serviceCategory?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.status) {
      filtered = filtered?.filter(rfq => rfq?.status === filters?.status);
    }

    if (filters?.category) {
      filtered = filtered?.filter(rfq => 
        rfq?.serviceCategory?.toLowerCase()?.includes(filters?.category?.toLowerCase())
      );
    }

    if (filters?.dateRange) {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          filtered = filtered?.filter(rfq => new Date(rfq.submissionDate) >= filterDate);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          filtered = filtered?.filter(rfq => new Date(rfq.submissionDate) >= filterDate);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          filtered = filtered?.filter(rfq => new Date(rfq.submissionDate) >= filterDate);
          break;
        case 'quarter':
          filterDate?.setMonth(now?.getMonth() - 3);
          filtered = filtered?.filter(rfq => new Date(rfq.submissionDate) >= filterDate);
          break;
        case 'year':
          filterDate?.setFullYear(now?.getFullYear() - 1);
          filtered = filtered?.filter(rfq => new Date(rfq.submissionDate) >= filterDate);
          break;
      }
    }

    setFilteredRfqs(filtered);
  }, [filters, rfqs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      category: '',
      dateRange: ''
    });
  };

  const handleViewDetails = (rfq) => {
    setSelectedRfq(rfq);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadQuote = (rfq) => {
    // Simulate quote download
    const link = document.createElement('a');
    link.href = '#';
    link.download = `Quote_${rfq?.rfqNumber}.pdf`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    
    // Show success message (in real app, use toast notification)
    alert(`Quote for ${rfq?.rfqNumber} downloaded successfully!`);
  };

  const handleAcceptQuote = (rfq) => {
    // Update RFQ status to accepted
    const updatedRfqs = rfqs?.map(r => 
      r?.id === rfq?.id ? { ...r, status: 'accepted' } : r
    );
    setRfqs(updatedRfqs);
    setIsDetailsModalOpen(false);
    
    // Show success message
    alert(`Quote ${rfq?.rfqNumber} has been accepted! You will be redirected to complete the order.`);
  };

  const handleRequestModification = (rfq) => {
    // In real app, this would open a modification form or redirect
    alert(`Modification request for ${rfq?.rfqNumber} will be sent to your sales representative.`);
    setIsDetailsModalOpen(false);
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', icon: 'Home' },
    { label: 'Dashboard', path: '/customer-dashboard', icon: 'LayoutDashboard' },
    { label: 'My RFQs', path: '/rfq-management', icon: 'FileText', isLast: true }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="mx-auto text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">Loading your RFQs...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <BreadcrumbNavigation customBreadcrumbs={breadcrumbs} />
        
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-foreground mb-2">RFQ Management</h1>
            <p className="text-muted-foreground">
              Track and manage your quotation requests with real-time status updates
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link to="/rfq-request-form">
              <Button variant="default" iconName="Plus" iconPosition="left">
                New RFQ Request
              </Button>
            </Link>
            <Link to="/product-catalog">
              <Button variant="outline" iconName="Package" iconPosition="left">
                Browse Services
              </Button>
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        <RFQSummaryCards summaryData={summaryData} />

        {/* Filters */}
        <RFQFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          totalCount={filteredRfqs?.length}
        />

        {/* RFQ Table */}
        <RFQTable
          rfqs={filteredRfqs}
          onViewDetails={handleViewDetails}
          onDownloadQuote={handleDownloadQuote}
          onAcceptQuote={handleAcceptQuote}
          onRequestModification={handleRequestModification}
        />

        {/* Pagination */}
        {filteredRfqs?.length > 10 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-muted-foreground">
              Showing 1 to {Math.min(10, filteredRfqs?.length)} of {filteredRfqs?.length} results
            </p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" disabled iconName="ChevronLeft">
                Previous
              </Button>
              <Button variant="outline" size="sm" className="bg-primary text-primary-foreground">
                1
              </Button>
              <Button variant="outline" size="sm" disabled iconName="ChevronRight">
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/rfq-request-form" className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <Icon name="Plus" size={20} className="text-primary" />
              <div>
                <p className="font-medium text-foreground">Create New RFQ</p>
                <p className="text-sm text-muted-foreground">Submit a new quotation request</p>
              </div>
            </Link>
            <Link to="/product-catalog" className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <Icon name="Package" size={20} className="text-primary" />
              <div>
                <p className="font-medium text-foreground">Browse Services</p>
                <p className="text-sm text-muted-foreground">Explore our service catalog</p>
              </div>
            </Link>
            <Link to="/customer-dashboard" className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <Icon name="BarChart3" size={20} className="text-primary" />
              <div>
                <p className="font-medium text-foreground">View Analytics</p>
                <p className="text-sm text-muted-foreground">Check your RFQ performance</p>
              </div>
            </Link>
          </div>
        </div>

        {/* RFQ Details Modal */}
        <RFQDetailsModal
          rfq={selectedRfq}
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          onDownloadQuote={handleDownloadQuote}
          onAcceptQuote={handleAcceptQuote}
          onRequestModification={handleRequestModification}
        />
      </div>
    </div>
  );
};

export default RFQManagement;